import type { ReactNode } from "react";

/**
 * The frame every xano screen sits in.
 *
 * This skin has no scenery — where the hatch skin draws a mountain and a cloud
 * sea, this one draws a dark ground, a hairline grid, and a single wash of blue
 * behind the top of the page. That is the whole backdrop, and it is here rather
 * than in each screen because three of the four things this component owns are
 * invisible when they are missing:
 *
 *   `xano-skin`  — what the reduced-motion sweep in index.css hangs off. Written
 *   as a sweep over the root precisely because the animations are arbitrary
 *   values with no class names to enumerate, so a screen that omitted the root
 *   would silently ignore the setting rather than fail visibly.
 *
 *   `deck-safe`  — the safe-area inset. Nothing else in `frontend/src` does this,
 *   and its absence looks perfect everywhere except on the phone the QR code
 *   sends people to.
 *
 *   `overflow-x-clip` — the axis nothing in this design is meant to be reached
 *   by scrolling. `clip` rather than `hidden` so this never becomes a scroll
 *   container, which would break the backdrop's anchoring.
 *
 * A screen that forgets any of them is a screen that looks right on the machine
 * it was built on.
 */
export function XanoScreen({
  fill = false,
  children,
}: {
  /**
   * Exactly viewport height, with the panes inside scrolling rather than the
   * page. The build and done screens need it; the landing screen must not have
   * it.
   *
   * That is not styling. With a minimum height instead of a fixed one, a pane
   * holding a growing build log grows the document rather than scrolling inside
   * itself, and a long build becomes an unbounded page where the whole history
   * is on screen at once and "now" is impossible to find. Both other skins
   * carry this note against the same bug.
   *
   * The landing screen is the other case: its content is a fixed amount that can
   * exceed a short window, and clamping it would clip the starter chips rather
   * than let them scroll.
   */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`xano-skin relative isolate overflow-x-clip bg-deck font-deck text-deck-text ${
        fill ? "h-dvh overflow-y-hidden" : "min-h-dvh"
      }`}
    >
      {/* The grid, held well back. At full strength it reads as a table; at this
          weight it reads as graph paper, which is the difference between a
          surface you can put a diagram on and a diagram of nothing. */}
      <div aria-hidden className="deck-grid pointer-events-none absolute inset-0 -z-10 opacity-60" />

      {/* One wash of blue behind the top of the page — the only thing in the
          backdrop that is not a straight line, and the reason the headline sits
          on something rather than on a flat black rectangle. Tall enough to
          reach past the fold on a phone, so it reads as light in the room rather
          than as a band across the screen. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[min(58vh,520px)]"
        style={{
          background:
            "radial-gradient(120% 100% at 50% 0%, var(--color-deck-blue-glow) 0%, transparent 68%)",
        }}
      />

      {/* The safe area is kept here and the paint above bleeds under it. See
          `.deck-safe` in index.css for why that split matters. */}
      <div className="deck-safe flex h-full min-h-0 flex-col">{children}</div>
    </div>
  );
}

/**
 * The Xano wordmark.
 *
 * Real path data, lifted from `cloud-frontend/src/assets/images/xano-blue.svg`
 * rather than redrawn — the same rule the palette follows, and it matters more
 * here: a hand-approximated wordmark is the one asset where "close" reads as
 * counterfeit at exactly the distance a booth is viewed from.
 *
 * The two-tone treatment is Xano's own for dark backgrounds — see
 * `xano-logo.svg` in the same directory, which fills the mark white and the X's
 * two right-hand arms a muted grey. Reproduced here with `currentColor` for the
 * mark, so a caller can recolour it, and a fixed muted tone for the arms.
 *
 * Inline rather than an `<img>`: no request, no flash of nothing on booth wifi,
 * and it inherits colour from the text around it.
 */
export function XanoLogo({ className = "h-4" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 68 16"
      role="img"
      aria-label="Xano"
      fill="currentColor"
      className={`${className} w-auto shrink-0`}
    >
      <path d="M29.6954 1.59191C29.5301 1.30145 29.3081 1.04616 29.0425 0.840753C28.7317 0.611853 28.3524 0.492064 27.9648 0.500408C27.5783 0.493343 27.2001 0.610752 26.8872 0.83488C26.6167 1.03649 26.3912 1.29098 26.2247 1.58249L19.7715 12.2094L22.5086 14.8006L27.8777 5.80538L30.172 9.61295H26.7427L25.1873 12.2713L31.7695 12.2849L33.6105 15.3353H38.0698L29.6954 1.59191Z" />
      <path d="M46.1013 0.573594V9.32558L37.8045 1.19446C37.603 0.987379 37.3661 0.81701 37.1048 0.691184C36.8561 0.577054 36.5852 0.517951 36.311 0.518008C36.0455 0.510707 35.7817 0.561761 35.5385 0.667481C35.2954 0.773202 35.0792 0.930947 34.9053 1.12932C34.5676 1.54148 34.3933 2.06089 34.4148 2.59067V8.11443L38.0448 14.1157V6.59996L46.3051 14.8055C46.7722 15.2659 47.2978 15.4997 47.8702 15.4997C48.1283 15.5045 48.3844 15.4532 48.6204 15.3496C48.8562 15.246 49.0662 15.0924 49.2356 14.8998C49.5647 14.5286 49.7315 14.0316 49.7315 13.4248V0.573594H46.1013Z" />
      <path d="M67.4696 4.97898C67.1357 4.12038 66.6229 3.34065 65.9643 2.69033C65.3058 2.04 64.5162 1.5336 63.6469 1.20391C62.7129 0.847813 61.7191 0.669898 60.7181 0.679602H57.448C56.4724 0.67144 55.504 0.845774 54.594 1.1934C53.7349 1.52537 52.9521 2.02462 52.2923 2.66144C51.6269 3.3114 51.1065 4.09153 50.7644 4.9518C50.3951 5.88182 50.2102 6.87326 50.2198 7.87217C50.2123 8.87545 50.3946 9.8713 50.7571 10.8086C51.0986 11.6906 51.6135 12.4971 52.2722 13.1817C52.9263 13.8528 53.7082 14.3898 54.5729 14.762C55.481 15.152 56.4621 15.3491 57.4523 15.3408H60.7203C62.1795 15.3634 63.6102 14.9405 64.8169 14.1299C66.0236 13.3193 66.9475 12.1604 67.4623 10.8117C67.8251 9.87453 68.0073 8.87863 67.9995 7.87532C68.0106 6.8854 67.8308 5.90244 67.4696 4.97898ZM60.7202 11.641H57.448C56.9684 11.6457 56.493 11.5527 56.0514 11.3678C55.6387 11.1948 55.2663 10.9404 54.9567 10.6202C54.6404 10.2869 54.3943 9.89479 54.2327 9.46669C54.0514 8.99155 53.9607 8.48739 53.9652 7.97966C53.9582 7.4733 54.0483 6.97021 54.2305 6.49687C54.3937 6.07847 54.6391 5.69606 54.9525 5.37163C55.2624 5.05622 55.6341 4.80645 56.0449 4.63754C56.4902 4.45972 56.9672 4.372 57.4474 4.37955H60.7197C61.2109 4.37412 61.6982 4.46526 62.1534 4.64764C62.5745 4.81369 62.9557 5.06514 63.2723 5.38577C63.5888 5.7064 63.8338 6.08906 63.9913 6.50903C64.1732 6.97982 64.264 7.48016 64.2589 7.98403C64.2633 8.49177 64.1726 8.99593 63.9913 9.47105C63.8288 9.89808 63.5807 10.2883 63.2619 10.6183C62.945 10.9399 62.5659 11.1954 62.147 11.3694C61.6951 11.5549 61.2095 11.6472 60.7202 11.641Z" />
      <path d="M6.48936 0.682727H0L7.8387 8.00955L0 15.3353H6.48936L14.3298 8.00955L6.48936 0.682727Z" />
      {/* The X's two right-hand arms, held back the way the brand's own
          dark-background lockup holds them back. */}
      <g fill="#a7a9ac">
        <path d="M14.7845 7.58275L22.1679 0.682727H15.6785L11.5399 4.55108L14.7602 7.55954L14.7845 7.58275Z" />
        <path d="M14.7602 8.45837L11.5399 11.468L15.6785 15.3353H22.1679L14.7845 8.43529L14.7602 8.45837Z" />
      </g>
    </svg>
  );
}

/**
 * The header both post-landing screens share: the lockup, the prompt, and one
 * changing slot on the right.
 *
 * Lives here rather than beside the build view because the done screen needs it
 * too, and importing it from there would make a cycle — BuildView pulls in
 * DoneView, DoneView would pull Header back out of BuildView. Shared chrome
 * belongs in the shared chrome module. Declared as a function rather than a
 * `const` arrow for the same reason: hoisting is what keeps a cycle from
 * becoming a temporal-dead-zone crash rather than a slow import.
 */
export function Header({
  prompt,
  children,
}: {
  prompt: string | null;
  children?: ReactNode;
}) {
  return (
    <header className="flex shrink-0 items-center gap-3 border-b border-deck-line px-4 py-3 sm:gap-4 sm:px-6">
      <XanoLogo className="h-[15px]" />
      {/* Four minutes into a build the visitor has lost the thread of what they
          asked for; the prompt is the answer to "what is this?". */}
      <p
        className="min-w-0 flex-1 truncate text-[13px] text-deck-text-soft"
        title={prompt ?? ""}
      >
        {prompt}
      </p>
      {children}
    </header>
  );
}
